"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import { NewsletterModal } from "@/components/newsLetterModal/NewsletterModal";
import { AdsterraSocialBar } from "@/components/ads/AdsterraSocialBar";
import { AdsterraBanner } from "@/components/ads/AdsterraBanner";
import { ADSTERRA_CONFIG } from "@/config/adsterra";
import { isTechNewsDomain } from "@/components/sites/technews-shared/theme";

// Lazy load domain-specific components with SSR enabled
const NewsIconsHeader = dynamic(() => import("./sites/newsicons/NewsIconsHeader"), {
  ssr: true
});
const NewsIconsFooter = dynamic(() => import("./sites/newsicons/NewsIconsFooter"), {
  ssr: true
});
const JejuTimeHeader = dynamic(() => import("./sites/jejutime/JejuTimeHeader"), {
  ssr: true
});
const JejuTimeFooter = dynamic(() => import("./sites/jejutime/JejuTimeFooter"), {
  ssr: true
});
const JejuQQHeader = dynamic(() => import("./sites/jejuqq/JejuQQHeader"), {
  ssr: true
});
const JejuQQFooter = dynamic(() => import("./sites/jejuqq/JejuQQFooter"), {
  ssr: true
});
const JejuJapanHeader = dynamic(() => import("./sites/jejujapan/JejuJapanHeader"), {
  ssr: true
});
const JejuJapanFooter = dynamic(() => import("./sites/jejujapan/JejuJapanFooter"), {
  ssr: true
});
const VoiceJejuHeader = dynamic<{
  onOpenNewsletter?: () => void;
}>(() => import("@/components/sites/voicejeju/VoiceJejuHeader").then(m => m.VoiceJejuHeader), {
  ssr: true
});
const VoiceJejuFooter = dynamic<{
  onOpenNewsletter?: () => void;
  footerBanners?: import("@/components/sites/voicejeju/VoiceJejuFooter").Banner[];
}>(() => import("@/components/sites/voicejeju/VoiceJejuFooter").then(m => m.VoiceJejuFooter), {
  ssr: true
});
const SkyBluePrimeHeader = dynamic(() => import("./sites/skyblueprime/SkyBluePrimeHeader"), {
  ssr: true
});
const SkyBluePrimeFooter = dynamic(() => import("./sites/skyblueprime/SkyBluePrimeFooter"), {
  ssr: true
});
const LavagueTechHeader = dynamic(() => import("./sites/lavaguetech/LavagueTechHeader"), {
  ssr: true
});
const LavagueTechFooter = dynamic(() => import("./sites/lavaguetech/LavagueTechFooter"), {
  ssr: true
});
const LegalHyperHeader = dynamic<{
  onOpenNewsletter?: () => void;
}>(() => import("@/components/sites/legalhyper/LegalHyperHeader").then(m => m.LegalHyperHeader), {
  ssr: true
});
const LegalHyperFooter = dynamic<{
  onOpenNewsletter?: () => void;
}>(() => import("@/components/sites/legalhyper/LegalHyperFooter").then(m => m.LegalHyperFooter), {
  ssr: true
});
// technews tenant family — one per-domain component set each
type TechNewsChromeProps = {
  domain: string;
  onOpenNewsletter?: () => void;
  categories?: string[];
};
const LinkTechNewsHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/linktechnews/LinkTechNewsHeader"), {
  ssr: true
});
const LinkTechNewsFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/linktechnews/LinkTechNewsFooter"), {
  ssr: true
});
const DbTechNewsHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/dbtechnews/DbTechNewsHeader"), {
  ssr: true
});
const DbTechNewsFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/dbtechnews/DbTechNewsFooter"), {
  ssr: true
});
const MagazineTechyHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/magazinetechy/MagazineTechyHeader"), {
  ssr: true
});
const MagazineTechyFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/magazinetechy/MagazineTechyFooter"), {
  ssr: true
});
const MagazineAirHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/magazineair/MagazineAirHeader"), {
  ssr: true
});
const MagazineAirFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/magazineair/MagazineAirFooter"), {
  ssr: true
});
const TechyGateHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/techygate/TechyGateHeader"), {
  ssr: true
});
const TechyGateFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/techygate/TechyGateFooter"), {
  ssr: true
});
const NewYorkSignalHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/newyorksignal/NewYorkSignalHeader"), {
  ssr: true
});
const NewYorkSignalFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/newyorksignal/NewYorkSignalFooter"), {
  ssr: true
});
const TechnikPostHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/technikpost/TechnikPostHeader"), {
  ssr: true
});
const TechnikPostFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/technikpost/TechnikPostFooter"), {
  ssr: true
});
const TechOggiHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/techoggi/TechOggiHeader"), {
  ssr: true
});
const TechOggiFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/techoggi/TechOggiFooter"), {
  ssr: true
});
const TechHoyHeader = dynamic<TechNewsChromeProps>(() => import("@/components/sites/techhoy/TechHoyHeader"), {
  ssr: true
});
const TechHoyFooter = dynamic<TechNewsChromeProps>(() => import("@/components/sites/techhoy/TechHoyFooter"), {
  ssr: true
});
const TECHNEWS_HEADERS: Record<string, React.ComponentType<TechNewsChromeProps>> = {
  "linktechnews.com": LinkTechNewsHeader,
  "dbtechnews.com": DbTechNewsHeader,
  "magazinetechy.com": MagazineTechyHeader,
  "magazineair.com": MagazineAirHeader,
  "techygate.com": TechyGateHeader,
  "newyorksignal.com": NewYorkSignalHeader,
  "technikpost.de": TechnikPostHeader,
  "techoggi.com": TechOggiHeader,
  "techhoy.com": TechHoyHeader
};
const TECHNEWS_FOOTERS: Record<string, React.ComponentType<TechNewsChromeProps>> = {
  "linktechnews.com": LinkTechNewsFooter,
  "dbtechnews.com": DbTechNewsFooter,
  "magazinetechy.com": MagazineTechyFooter,
  "magazineair.com": MagazineAirFooter,
  "techygate.com": TechyGateFooter,
  "newyorksignal.com": NewYorkSignalFooter,
  "technikpost.de": TechnikPostFooter,
  "techoggi.com": TechOggiFooter,
  "techhoy.com": TechHoyFooter
};
function technewsKey(domain: string): string {
  const d = domain.toLowerCase().replace(/^www\./, "");
  return Object.keys(TECHNEWS_HEADERS).find(k => d.includes(k.replace(/\.(com|de)$/, ""))) ?? "linktechnews.com";
}

// Fallbacks
const DefaultHeader = dynamic(() => import("./Header").then(m => m.Header), {
  ssr: true
});
const DefaultFooter = dynamic(() => import("./Footer").then(m => m.Footer), {
  ssr: true
});
interface SiteShellProps {
  children: React.ReactNode;
  domain: string;
  /** techoggi.com only: its real-time, MediaStack-classified category list
   *  (computed server-side in layout.tsx), overriding the static
   *  getCoreCategories(domain) list its Header/Footer otherwise fall back to. */
  categories?: string[];
}
export function SiteShell(t0) {
  const $ = _c(32);
  const {
    children,
    domain,
    categories
  } = t0;
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsNewsletterOpen(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const openNewsletter = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsNewsletterOpen(false);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const closeNewsletter = t2;
  let t3;
  bb0: {
    const d = domain.toLowerCase();
    if (d.includes("voicejeju")) {
      t3 = "voicejeju";
      break bb0;
    }
    if (d.includes("jejutime")) {
      t3 = "jejutime";
      break bb0;
    }
    if (d.includes("jejuqq")) {
      t3 = "jejuqq";
      break bb0;
    }
    if (d.includes("jejujapan")) {
      t3 = "jejujapan";
      break bb0;
    }
    if (d.includes("newsicons")) {
      t3 = "newsicons";
      break bb0;
    }
    if (d.includes("skyblueprime")) {
      t3 = "skyblueprime";
      break bb0;
    }
    if (d.includes("lavaguetech")) {
      t3 = "lavaguetech";
      break bb0;
    }
    if (d.includes("legalhyper")) {
      t3 = "legalhyper";
      break bb0;
    }
    if (isTechNewsDomain(d)) {
      t3 = "technews";
      break bb0;
    }
    t3 = "default";
  }
  const site = t3;
  let t4;
  if ($[2] !== categories || $[3] !== domain || $[4] !== site) {
    t4 = () => {
      switch (site) {
        case "voicejeju":
          {
            return <VoiceJejuHeader onOpenNewsletter={openNewsletter} />;
          }
        case "jejutime":
          {
            return <JejuTimeHeader onOpenNewsletter={openNewsletter} />;
          }
        case "jejuqq":
          {
            return <JejuQQHeader onOpenNewsletter={openNewsletter} />;
          }
        case "jejujapan":
          {
            return <JejuJapanHeader onOpenNewsletter={openNewsletter} />;
          }
        case "newsicons":
          {
            return <NewsIconsHeader onOpenNewsletter={openNewsletter} />;
          }
        case "skyblueprime":
          {
            return <SkyBluePrimeHeader onOpenNewsletter={openNewsletter} />;
          }
        case "lavaguetech":
          {
            return <LavagueTechHeader onOpenNewsletter={openNewsletter} />;
          }
        case "legalhyper":
          {
            return <LegalHyperHeader onOpenNewsletter={openNewsletter} />;
          }
        case "technews":
          {
            const H = TECHNEWS_HEADERS[technewsKey(domain)];
            return <H domain={domain} onOpenNewsletter={openNewsletter} categories={categories} />;
          }
        default:
          {
            return <DefaultHeader onOpenNewsletter={openNewsletter} />;
          }
      }
    };
    $[2] = categories;
    $[3] = domain;
    $[4] = site;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const renderHeader = t4;
  let t5;
  if ($[6] !== categories || $[7] !== domain || $[8] !== site) {
    t5 = () => {
      switch (site) {
        case "voicejeju":
          {
            return <VoiceJejuFooter onOpenNewsletter={openNewsletter} />;
          }
        case "jejutime":
          {
            return <JejuTimeFooter onOpenNewsletter={openNewsletter} />;
          }
        case "jejuqq":
          {
            return <JejuQQFooter onOpenNewsletter={openNewsletter} />;
          }
        case "jejujapan":
          {
            return <JejuJapanFooter onOpenNewsletter={openNewsletter} />;
          }
        case "newsicons":
          {
            return <NewsIconsFooter onOpenNewsletter={openNewsletter} />;
          }
        case "skyblueprime":
          {
            return <SkyBluePrimeFooter onOpenNewsletter={openNewsletter} />;
          }
        case "lavaguetech":
          {
            return <LavagueTechFooter onOpenNewsletter={openNewsletter} />;
          }
        case "legalhyper":
          {
            return <LegalHyperFooter onOpenNewsletter={openNewsletter} />;
          }
        case "technews":
          {
            const F = TECHNEWS_FOOTERS[technewsKey(domain)];
            return <F domain={domain} onOpenNewsletter={openNewsletter} categories={categories} />;
          }
        default:
          {
            return <DefaultFooter onOpenNewsletter={openNewsletter} />;
          }
      }
    };
    $[6] = categories;
    $[7] = domain;
    $[8] = site;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const renderFooter = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="h-14 md:h-16 bg-white border-b border-gray-100" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== renderHeader) {
    t7 = <React.Suspense fallback={t6}>{renderHeader()}</React.Suspense>;
    $[11] = renderHeader;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== site) {
    t8 = site === "newsicons" && <div className="w-full flex justify-center py-3 border-b border-slate-200 overflow-hidden bg-white"><div className="hidden sm:block"><AdsterraBanner bannerKey={ADSTERRA_CONFIG.newsicons.banners["728x90"]} width={728} height={90} className="!my-0" /></div><div className="block sm:hidden"><AdsterraBanner bannerKey={ADSTERRA_CONFIG.newsicons.banners["320x50"]} width={320} height={50} className="!my-0" /></div></div>;
    $[13] = site;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== children) {
    t9 = <main className="flex-1">{children}</main>;
    $[15] = children;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="h-48 bg-gray-50" />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== renderFooter) {
    t11 = <React.Suspense fallback={t10}>{renderFooter()}</React.Suspense>;
    $[18] = renderFooter;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== domain || $[21] !== isNewsletterOpen) {
    t12 = <NewsletterModal isOpen={isNewsletterOpen} onClose={closeNewsletter} domain={domain} />;
    $[20] = domain;
    $[21] = isNewsletterOpen;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== domain) {
    t13 = <AdsterraSocialBar domain={domain} />;
    $[23] = domain;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t14 = <div className="flex flex-col min-h-screen">{t7}{t8}{t9}{t11}{t12}{t13}</div>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}